import {Component, Inject} from '@angular/core';
import {DIALOG_DATA, DialogRef} from '@angular/cdk/dialog';
import {CommonModule} from '@angular/common';

/**
 * Codec fourcc -> the name an operator would use.
 *
 * Keyed on the leading fourcc of a manifest codec string ("avc1.640015" -> "avc1"), because the
 * profile/level suffix differs per rendition and says nothing a viewer of this dialog needs.
 */
const CODEC_NAMES: Record<string, string> = {
  avc1: 'H.264',
  avc3: 'H.264',
  hvc1: 'HEVC',
  hev1: 'HEVC',
  av01: 'AV1',
  vp09: 'VP9',
  vp9: 'VP9',
};

@Component({
  selector: 'app-player-select-video',
  templateUrl: './player-select-video.component.html',
  styleUrls: ['./player-select-video.component.scss'],
  imports: [
    CommonModule,
  ]
})
export class PlayerSelectVideoComponent {
  currentlySelectedRendition: Number;
  renditions: any[];
  activeRenditionId: Number;

  constructor(protected dialogRef: DialogRef<Number>,
              @Inject(DIALOG_DATA) data: {
                renditions: any[],
                activeRenditionId: Number,
              }) {
    this.renditions = data.renditions ?? [];
    this.activeRenditionId = data.activeRenditionId;
    this.currentlySelectedRendition = data.activeRenditionId
  }

  /**
   * What one rendition is, in the terms it differs from the others by.
   *
   * One label for DASH and HLS. The old template printed the literal "HLS VIDEO" for every HLS
   * rendition, which named the container rather than the rendition -- but `clpp.Rendition` carries
   * `width`, `height`, `bandwidth` and `codec` for HLS exactly as it does for DASH, straight off
   * `RESOLUTION`, `BANDWIDTH` and `CODECS` in the master playlist. Nothing was missing; the branch
   * was simply never written.
   *
   * The codec belongs in the label, not just the resolution. A multi-codec ladder puts the same
   * resolution in the list more than once -- H.264 and HEVC both carry 854, 1024 and 1280 -- so
   * "853×480 @1.6 Mbit/s" alone would render two different renditions as near-duplicates and the
   * choice would be a guess.
   *
   * Every field is optional in the player's model, so each part is dropped when absent rather than
   * printed as "null×null". A rendition that reports nothing at all still gets its id, because an
   * unlabelled radio button cannot be chosen deliberately.
   */
  renditionLabel(rendition: any): string {
    const parts = [
      this.codecName(rendition?.codec),
      this.resolution(rendition),
      this.bitrate(rendition?.bandwidth),
    ].filter(part => !!part);
    return parts.length ? parts.join(' · ') : `Rendition ${rendition?.id ?? '?'}`;
  }

  /** Whether anything in the list is listed-but-unplayable, so the note is only shown when true. */
  hasUnplayable(): boolean {
    return this.renditions.some(r => !this.isPlayable(r));
  }

  /**
   * Can this rendition actually be selected?
   *
   * A choice coming from the manifest rather than the player has no id and cannot be played -- most
   * often encrypted HEVC in Chrome, where Widevine offers no `hvc1` capability even though the
   * browser decodes HEVC in the clear. It is still listed, because the point of the preview is to
   * confirm what was encoded; it is just not selectable.
   */
  isPlayable(rendition: any): boolean {
    return rendition?.playable !== false && rendition?.id !== null && rendition?.id !== undefined;
  }

  /** "avc1.640015" -> "H.264". An unmapped codec keeps its fourcc, which still beats nothing. */
  private codecName(codec: string | null | undefined): string | null {
    if (!codec) {
      return null;
    }
    const fourcc = String(codec).split('.')[0].toLowerCase();
    return CODEC_NAMES[fourcc] ?? fourcc;
  }

  private resolution(rendition: any): string | null {
    const {width, height} = rendition ?? {};
    return width && height ? `${width}×${height}` : null;
  }

  /**
   * Bits per second as the operator reads it. Divided by 1000, not 1024: manifest bandwidth is
   * decimal bits per second, so the old `/1024` under-reported every DASH rendition by ~2.4%.
   */
  private bitrate(bandwidth: number | null | undefined): string | null {
    if (!bandwidth) {
      return null;
    }
    return bandwidth >= 1_000_000
      ? `${(bandwidth / 1_000_000).toFixed(1)} Mbit/s`
      : `${Math.round(bandwidth / 1000)} kbit/s`;
  }

}
